import { useCallback, useEffect, useState } from "react"

import { Category, GroupedCategories } from "@doktor-se/bones-ui/dist/web-shared/types"

import { groupCategories } from "lib/format"
import { useAppSelector, useSubcategories } from "lib/hooks"
import { selectCategoriesPerRole } from "reducers/categories"

import SearchCategories from "./SearchCategories"

interface SearchCategoriesWrapperProps {
  onSelectCategory: (category: Category) => void
  filterCategoriesForProfile?: boolean
  additionalCategoriesFilter?: (categories: Category[]) => Category[]
  showSubcategoriesHeaders?: boolean
}

const filterSubCategories = (
  categories: Category[],
  profileType: "profile" | "client",
  additionalCategoriesFilter?: (categories: Category[]) => Category[]
) =>
  categories.reduce(
    (acc, category) => {
      let children = categories.filter(c => c.parentId === category.id && c.metadata.patientType === profileType)
      if (additionalCategoriesFilter) {
        children = additionalCategoriesFilter(children)
      }
      if (!!children.length) acc[category.id] = groupCategories(children)
      return acc
    },
    {} as { [category: number]: GroupedCategories[] }
  )

const SearchCategoriesWrapper = ({
  onSelectCategory,
  filterCategoriesForProfile = false,
  additionalCategoriesFilter,
  showSubcategoriesHeaders = true
}: SearchCategoriesWrapperProps) => {
  const categories = useAppSelector(selectCategoriesPerRole)
  const [filteredCategories, setFilteredCategories] = useState<GroupedCategories[]>([])
  const [filteredSubcategories, setFilteredSubcategories] = useState<{ [categoryId: number]: GroupedCategories[] }>({})
  const subcategories = useSubcategories(categories)
  const [selectedParentCategory, setSelectedParentCategory] = useState<Category>()
  const filterCategories = useCallback(() => {
    const patientType = filterCategoriesForProfile ? "profile" : "client"
    let filtered = categories.filter(c => c.metadata.patientType === patientType)
    if (additionalCategoriesFilter) {
      filtered = additionalCategoriesFilter(filtered)
    }
    const filteredSub = filterSubCategories(categories, patientType, additionalCategoriesFilter)

    setFilteredSubcategories(filteredSub)
    setFilteredCategories(
      groupCategories(filtered.filter(c => !c.parentId && !Object.keys(filteredSub).includes(c.id.toString())))
    )
  }, [categories, filterCategoriesForProfile, additionalCategoriesFilter])

  useEffect(() => {
    filterCategories()
  }, [filterCategories])

  const categoryOnClick = (category: Category) => {
    if (subcategories[category.id]) {
      setFilteredCategories(subcategories[category.id])
      setSelectedParentCategory(category)
    } else {
      onSelectCategory(category)
    }
  }

  const closeSubcategory = () => {
    filterCategories()
    setSelectedParentCategory(undefined)
  }

  return (
    <SearchCategories
      categories={filteredCategories}
      subcategories={Object.keys(filteredSubcategories).map(id => categories.find(c => c.id.toString() === id)!)}
      changeCategory={category => categoryOnClick(category)}
      closeSubcategory={closeSubcategory}
      selectedParentCategory={selectedParentCategory}
      showSubcategoriesHeaders={showSubcategoriesHeaders}
    />
  )
}

export default SearchCategoriesWrapper
